import {
  BoundNumberField,
  BoundTextField,
  Css,
  GridDataRow,
  GridTableLayout,
  IconButton,
  PageHeaderLayout,
  actionColumn,
  column,
  emptyCell,
  numericColumn,
  simpleHeader,
  useGridTableLayoutState,
  useModal,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, useFormStates } from "@homebound/form-state";
import { useCallback, useMemo } from "react";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { FeatureFlagType, SaveGlobalPlanConstraintItemInput } from "src/generated/graphqlTypes";
import { useSaveGlobalPlanConstraintItemMutation } from "src/routes/libraries/task-catalog/components/AddConstraintItemModal.generated";
import {
  ConstraintAndAllowanceCatalogPage_GlobalPlanConstraintItemFragment,
  useConstraintAndAllowanceCatalogPageQuery,
  useDeleteGlobalPlanConstraintItemMutation,
} from "src/routes/libraries/task-catalog/ConstraintItemCatalogPage.generated";
import { catalogTasksRoute } from "src/routing/domains/libraries/task-catalog/taskCatalogPaths";
import { AddConstraintItemModal, ConstraintModalConfig } from "./components/AddConstraintItemModal";

export function ConstraintItemCatalogPage() {
  const { data } = useConstraintAndAllowanceCatalogPageQuery();
  const { openModal } = useModal();
  const [saveGlobalPlanConstraintItem] = useSaveGlobalPlanConstraintItemMutation();
  const [deleteGlobalPlanConstraintItem] = useDeleteGlobalPlanConstraintItemMutation();
  const isReadonly = !useFeatureFlag(FeatureFlagType.TaskCatalogAdmin);
  const layoutState = useGridTableLayoutState({
    persistedFilter: { filterDefs: {}, storageKey: "constraintItemCatalogFilter" },
    search: "client",
  });

  const { getFormState } = useFormStates<
    SaveGlobalPlanConstraintItemInput,
    ConstraintAndAllowanceCatalogPage_GlobalPlanConstraintItemFragment
  >({
    config: formConfig,
    readOnly: isReadonly,
    getId: (v) => v.id,
    autoSave: async (os) => {
      await saveGlobalPlanConstraintItem({ variables: { input: os.changedValue } });
      os.commitChanges();
    },
  });

  const onDelete = useCallback(
    async (id: string) => {
      await deleteGlobalPlanConstraintItem({
        variables: { input: { id } },
        update: (cache) => cache.evict({ id: cache.identify({ __typename: "GlobalPlanConstraintItem", id }) }),
      });
    },
    [deleteGlobalPlanConstraintItem],
  );

  const columns = useMemo(
    () => createColumns(getFormState, onDelete, isReadonly),
    [getFormState, onDelete, isReadonly],
  );
  const rows = useMemo(() => createRows(data?.globalPlanConstraintItems ?? []), [data]);

  return (
    <PageHeaderLayout
      pageHeader={{
        title: "Constraint/ Allowance Catalog",
        breadcrumbs: { breadcrumbs: [{ href: catalogTasksRoute.url(), label: "Task Catalog" }] },
        actions: !isReadonly
          ? [
              {
                label: "Create",
                onClick: () =>
                  openModal({
                    content: <AddConstraintItemModal constraintType={ConstraintModalConfig.ConstraintItem} />,
                  }),
              },
            ]
          : undefined,
      }}
    >
      <p css={Css.pxPx(24).sm.layoutContainer.$}>
        Constraints and allowances share the same items, but can be used as buffers either before or after a task.
      </p>
      <GridTableLayout
        hideEditColumns
        layoutState={layoutState}
        tableProps={{ columns, rows, as: "virtual", rowStyles: { data: { cellCss: Css.px3.$ } } }}
      />
    </PageHeaderLayout>
  );
}

type HeaderRow = { kind: "header" };
type DataRow = { kind: "data"; data: ConstraintAndAllowanceCatalogPage_GlobalPlanConstraintItemFragment };

type Row = HeaderRow | DataRow;

function createColumns(
  getFormState: (
    input: ConstraintAndAllowanceCatalogPage_GlobalPlanConstraintItemFragment,
  ) => ObjectState<SaveGlobalPlanConstraintItemInput>,
  onDelete: (id: string) => void,
  isReadonly: boolean,
) {
  return [
    column<Row>({
      header: "Constraint/Allowance Name",
      data: (row) => ({
        content: () => {
          const os = getFormState(row);
          return <BoundTextField label="ConstraintOrAllowanceName" field={os.name} />;
        },
        value: () => row.name,
      }),
      mw: "240px",
    }),
    numericColumn<Row>({
      header: "Lag / Lead",
      data: (row) => {
        const os = getFormState(row);

        // readonly version of the boundnumberfield looks terrible in the table, so adding a simple span instead
        if (isReadonly) {
          return <span css={Css.sm.$}>{os.lagInDays.value}</span>;
        } else {
          return <BoundNumberField label="lagInDays" type="days" field={os.lagInDays} />;
        }
      },
      mw: "140px",
    }),
    column<Row>({
      header: "Times Used",
      data: ({ numberOfTimesUsed }) => <span css={Css.sm.$}>{numberOfTimesUsed}</span>,
      mw: "120px",
    }),
    // only current action is delete, so only include action column if not readonly
    ...(!isReadonly
      ? [
          actionColumn<Row>({
            header: emptyCell,
            data: (row) => (
              <IconButton
                icon="trash"
                disabled={!row.canDelete.allowed}
                tooltip={row.canDelete.disabledReasons.map((r) => r.message).join(", ") || undefined}
                onClick={() => onDelete(row.id)}
              />
            ),
            w: "50px",
          }),
        ]
      : []),
  ].compact();
}

function createRows(data: ConstraintAndAllowanceCatalogPage_GlobalPlanConstraintItemFragment[]): GridDataRow<Row>[] {
  return [simpleHeader, ...data?.map((gpci) => ({ kind: "data" as const, id: gpci.id, data: gpci }))];
}

const formConfig: ObjectConfig<SaveGlobalPlanConstraintItemInput> = {
  id: { type: "value" },
  name: { type: "value" },
  lagInDays: { type: "value" },
};
